import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, RefreshCw, ExternalLink, CheckCircle2, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PropertyDuplicatesAuditPage — admin tool to find and merge duplicate
 * properties (same client + same normalized address). Surfaced after
 * Iter202 caught operators silently creating doubles via the
 * "Use Client Address" button.
 *
 * Flow:
 *  1. GET /api/admin/property-duplicates  → groups of ≥2 properties
 *  2. Operator picks a "keeper" per group (default: oldest by created_at)
 *  3. POST /api/admin/property-duplicates/merge  → re-points all
 *     appraisals + documents → loser docs soft-deleted with a
 *     `merged_into` tombstone pointing at the keeper.
 *  4. Group disappears from the list on success; toast confirms what
 *     moved (appraisal count, history entries copied).
 */
export default function PropertyDuplicatesAuditPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [keepIds, setKeepIds] = useState({}); // { groupKey: keeperPropertyId }
  const [mergingKey, setMergingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/property-duplicates');
      const grps = data?.groups || [];
      setGroups(grps);
      // Default keeper per group: the property with the MOST appraisals,
      // breaking ties by oldest created_at — minimizes data shuffling.
      const defaults = {};
      grps.forEach(g => {
        const sorted = [...g.properties].sort((a, b) => {
          if ((b.appraisal_count || 0) !== (a.appraisal_count || 0)) {
            return (b.appraisal_count || 0) - (a.appraisal_count || 0);
          }
          return String(a.created_at || '').localeCompare(String(b.created_at || ''));
        });
        defaults[g.key] = sorted[0]?._id;
      });
      setKeepIds(defaults);
    } catch (e) {
      toast.error('Failed to load duplicate audit');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const mergeGroup = async (group) => {
    const keepId = keepIds[group.key];
    if (!keepId) { toast.error('Pick a keeper first'); return; }
    const mergeIds = group.properties.map(p => p._id).filter(id => id !== keepId);
    if (mergeIds.length === 0) { toast.error('Nothing to merge'); return; }
    setMergingKey(group.key);
    try {
      const { data } = await api.post('/admin/property-duplicates/merge', {
        keep_id: keepId,
        merge_ids: mergeIds,
      });
      const parts = [];
      if (data.appraisals_moved) parts.push(`${data.appraisals_moved} appraisal${data.appraisals_moved === 1 ? '' : 's'}`);
      if (data.docs_moved) parts.push(`${data.docs_moved} document${data.docs_moved === 1 ? '' : 's'}`);
      if (data.history_copied) parts.push(`${data.history_copied} history entr${data.history_copied === 1 ? 'y' : 'ies'}`);
      toast.success(`Merged ${data.merged} propert${data.merged === 1 ? 'y' : 'ies'}${parts.length ? ` — re-pointed ${parts.join(', ')}` : ''}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setMergingKey(null);
    }
  };

  return (
    <div className="container mx-auto px-6 py-6 max-w-6xl">
      <div className="flex items-center gap-3 mb-2">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-800"><ArrowLeft size={18} /></button>
        <h1 className="text-2xl font-bold text-slate-900 font-[Manrope] flex-1">Property Duplicates Audit</h1>
        <button data-testid="refresh-btn" onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 rounded px-2.5 py-1.5 hover:bg-slate-50 disabled:opacity-50">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Groups of ≥2 properties that share the same client and normalized address (street number + name + suburb). Merging keeps the chosen property and re-points all appraisals + documents from the losers into the keeper before soft-deleting them.
      </p>

      {loading && !groups.length && (
        <div className="text-center py-12 text-slate-400">
          <Loader2 size={24} className="animate-spin inline" />
          <p className="mt-2 text-sm">Scanning all properties…</p>
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div data-testid="no-duplicates" className="text-center py-12 border border-dashed border-slate-200 rounded-lg bg-slate-50/50">
          <CheckCircle2 size={32} className="text-emerald-500 mx-auto" />
          <p className="text-base font-semibold text-slate-800 mt-2">No duplicate properties found</p>
          <p className="text-sm text-slate-500 mt-1">Every property has a unique (client + address) fingerprint.</p>
        </div>
      )}

      <div className="space-y-4">
        {groups.map(g => {
          const keepId = keepIds[g.key];
          const isMerging = mergingKey === g.key;
          return (
            <div key={g.key} data-testid={`dupe-group-${g.key.slice(0, 12)}`} className="border border-amber-300 bg-amber-50/40 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-amber-600" />
                <span className="font-semibold text-slate-900 text-sm">{g.client_label}</span>
                {g.client_access_id && <span className="font-mono text-[11px] text-amber-700">C{g.client_access_id}</span>}
                <button
                  onClick={() => navigate(`/clients/${g.client_id}`)}
                  className="ml-auto text-[10px] inline-flex items-center gap-0.5 px-2 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-100"
                >Open client <ExternalLink size={9} /></button>
              </div>
              <div className="grid gap-2">
                {g.properties.map(p => {
                  const isKeeper = p._id === keepId;
                  const addr = [
                    p.property_address_num,
                    p.property_address_street,
                    p.property_address_suburb,
                    p.property_address_city,
                  ].filter(Boolean).join(' ').replace(/\s,/g, ',');
                  return (
                    <label
                      key={p._id}
                      data-testid={`dupe-row-${p._id}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded border cursor-pointer transition ${
                        isKeeper
                          ? 'border-emerald-400 bg-emerald-50/70 ring-2 ring-emerald-300/30'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`keep-${g.key}`}
                        checked={isKeeper}
                        onChange={() => setKeepIds(s => ({ ...s, [g.key]: p._id }))}
                        className="accent-emerald-600 w-4 h-4"
                        data-testid={`dupe-keep-radio-${p._id}`}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="font-semibold text-slate-800 text-sm">{addr || '(no address)'}</span>
                        {p.access_id && <span className="font-mono text-[10px] text-amber-700 ml-2">P{p.access_id}</span>}
                        {p.roof_type && <span className="text-[10px] text-slate-500 ml-2">· {p.roof_type}</span>}
                        <span className="block text-[11px] text-slate-500 mt-0.5">
                          {p.appraisal_count > 0
                            ? <strong className="text-slate-700">{p.appraisal_count} appraisal{p.appraisal_count === 1 ? '' : 's'}</strong>
                            : <span className="text-slate-400">0 appraisals</span>}
                          {p.created_at && <span className="ml-2">· created {String(p.created_at).slice(0, 10)}</span>}
                          {isKeeper && <span className="ml-2 inline-flex items-center gap-0.5 text-emerald-700 font-bold uppercase tracking-wider text-[9px]">✓ keep</span>}
                        </span>
                      </span>
                      <a
                        href={`/properties/${p._id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="text-[10px] inline-flex items-center gap-0.5 px-2 py-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-800 flex-shrink-0"
                      >Open <ExternalLink size={9} /></a>
                    </label>
                  );
                })}
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <span className="text-[11px] text-slate-500 mr-auto">
                  Will merge {g.properties.length - 1} propert{g.properties.length - 1 === 1 ? 'y' : 'ies'} into <span className="font-mono text-emerald-700 font-bold">P{g.properties.find(p => p._id === keepId)?.access_id || '?'}</span>
                </span>
                <button
                  data-testid={`dupe-merge-btn-${g.key.slice(0, 12)}`}
                  onClick={() => mergeGroup(g)}
                  disabled={isMerging || !keepId}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded disabled:opacity-40"
                >
                  {isMerging ? <Loader2 size={12} className="animate-spin" /> : <ChevronRight size={12} />}
                  {isMerging ? 'Merging…' : 'Merge into keeper'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
